// Het geheugen van de matcher per prijslijst inkijken en corrigeren (SPEC_calculatie 10.2–10.3).
// Een verkeerd geleerde match zou anders permanent en onzichtbaar blijven. Regels kunnen uitgezet
// (blijven bewaard, worden niet toegepast), naar een andere post verplaatst of vergeten worden.
// Wijzigen mag enkel wie beheerrecht heeft (Ervin en Timen, SPEC 27okt 7); de rest kijkt mee.
import { Fragment, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AppShell } from '../components/AppShell';
import { PricelistItemPicker } from '../components/PricelistItemPicker';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { fmtDate } from '../lib/format';

/** Aan/uit-schakelaar voor één regel. */
function Schakelaar({ aan, onChange, label, disabled }: { aan: boolean; onChange: (aan: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={aan}
      aria-label={label}
      title={aan ? 'Aan: wordt toegepast. Klik om uit te zetten.' : 'Uit: wordt niet toegepast. Klik om aan te zetten.'}
      disabled={disabled}
      onClick={() => onChange(!aan)}
      className={`relative inline-flex h-[20px] w-[36px] shrink-0 items-center rounded-pill transition-colors focus:outline-none focus-visible:shadow-focus disabled:opacity-50 ${aan ? 'bg-success' : 'bg-secondary-light'}`}
    >
      <span className={`inline-block h-[16px] w-[16px] rounded-pill bg-white shadow-xs transition-transform ${aan ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
    </button>
  );
}

export function LearnedMatches() {
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const magBeheren = !!user?.magBeheren;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [verplaatsId, setVerplaatsId] = useState<string | null>(null);

  const listQ = useQuery({ queryKey: ['pricelists'], queryFn: () => api.pricelists.list() });
  const pricelists = listQ.data?.pricelists ?? [];
  const pricelistId = activeId ?? pricelists[0]?.id ?? null;

  const learningQ = useQuery({
    queryKey: ['learning', pricelistId],
    queryFn: () => api.pricelists.learned(pricelistId!),
    enabled: !!pricelistId,
  });
  // Posten van deze prijslijst voor "verplaatsen naar andere post" — enkel nodig voor wie mag beheren.
  const detailQ = useQuery({
    queryKey: ['pricelist', pricelistId],
    queryFn: () => api.pricelists.detail(pricelistId!),
    enabled: !!pricelistId && magBeheren,
  });
  // NVT-posten worden door de matcher nooit via het geheugen toegepast: geen zinvol doel.
  const posten = useMemo(
    () => (detailQ.data?.pricelist.items ?? []).filter((it) => !it.isNvt && it.naamArbeid !== 'NVT'),
    [detailQ.data],
  );

  const invalidate = () => qc.invalidateQueries({ queryKey: ['learning', pricelistId] });
  const forgetMatch = useMutation({
    mutationFn: (id: string) => api.pricelists.forgetMatch(pricelistId!, id),
    onSuccess: () => { toast.success('Vergeten — wordt bij de volgende fiche opnieuw voorgesteld'); invalidate(); },
    onError: () => toast.error('Vergeten mislukt'),
  });
  const forgetSkip = useMutation({
    mutationFn: (id: string) => api.pricelists.forgetSkip(pricelistId!, id),
    onSuccess: () => { toast.success('Vergeten — komt bij de volgende fiche weer in de review-queue'); invalidate(); },
    onError: () => toast.error('Vergeten mislukt'),
  });
  const updateMatch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { actief?: boolean; plItemId?: string } }) => api.pricelists.updateLearned(pricelistId!, id, body),
    onSuccess: (_r, { body }) => {
      if (body.plItemId) { toast.success('Verplaatst — de tekst wordt voortaan aan de nieuwe post gekoppeld'); setVerplaatsId(null); }
      else toast.success(body.actief ? 'Aangezet — wordt weer toegepast' : 'Uitgezet — blijft bewaard maar wordt niet meer toegepast');
      invalidate();
    },
    onError: () => toast.error('Wijzigen mislukt'),
  });
  const updateSkip = useMutation({
    mutationFn: ({ id, actief }: { id: string; actief: boolean }) => api.pricelists.updateSkip(pricelistId!, id, { actief }),
    onSuccess: (_r, { actief }) => { toast.success(actief ? 'Aangezet — wordt weer overgeslagen' : 'Uitgezet — komt weer in de review-queue'); invalidate(); },
    onError: () => toast.error('Wijzigen mislukt'),
  });

  const f = filter.trim().toLowerCase();
  const matches = useMemo(() => {
    const rows = learningQ.data?.matches ?? [];
    const filtered = f
      ? rows.filter((r) => r.pdfNaamNormalized.includes(f) || r.plItemPdfNaam.toLowerCase().includes(f) || r.plItemNaamArbeid.toLowerCase().includes(f))
      : rows;
    return [...filtered].sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt));
  }, [learningQ.data, f]);
  const skips = useMemo(() => {
    const rows = learningQ.data?.skips ?? [];
    const filtered = f ? rows.filter((r) => r.pdfNaamNormalized.includes(f)) : rows;
    return [...filtered].sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt));
  }, [learningQ.data, f]);

  const aantalUit = (learningQ.data?.matches ?? []).filter((m) => m.actief === false).length + (learningQ.data?.skips ?? []).filter((s) => s.actief === false).length;

  return (
    <AppShell>
      <div className="mb-6">
        <div className="max-w-3xl">
          <h1 className="page-title">Geleerde matches</h1>
          <p className="page-subtitle">
            Wat de matcher onthield uit handmatige keuzes. Een fout hier wordt bij elke volgende fiche stil herhaald: zet de regel uit, verplaats ze naar de juiste post of vergeet ze.
          </p>
        </div>
      </div>

      {!magBeheren && (
        <div className="notice mb-5">
          Alleen-lezen: geleerde regels wijzigen kan enkel wie beheerrecht heeft (Ervin en Timen).
        </div>
      )}

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <select className="select w-auto max-w-xs" value={pricelistId ?? ''} onChange={(e) => { setActiveId(e.target.value); setVerplaatsId(null); }} aria-label="Prijslijst">
          {pricelists.map((pl) => (
            <option key={pl.id} value={pl.id}>{pl.name}</option>
          ))}
        </select>
        <input
          className="input max-w-sm"
          placeholder="Zoek op fiche-tekst of werkpost…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Zoek in het geheugen"
        />
        {learningQ.data && (
          <span className="ml-1 text-sm text-secondary tabular-nums">
            {learningQ.data.matches.length} matches · {learningQ.data.skips.length} genegeerde regels{aantalUit > 0 ? ` · ${aantalUit} uitgezet` : ''}
          </span>
        )}
      </div>

      <div className="card mb-5">
        <h2 className="text-base font-semibold mb-1">Onthouden matches</h2>
        <p className="text-xs text-secondary mb-3">
          Fiche-tekst (genormaliseerd) → werkpost uit de prijslijst. Wordt toegepast vóór de fuzzy-matcher, zonder review. Een uitgezette regel blijft hier staan maar wordt niet meer toegepast.
        </p>
        {learningQ.isLoading ? (
          <p className="text-sm text-secondary">Laden…</p>
        ) : matches.length === 0 ? (
          <p className="text-sm text-secondary">Nog niets geleerd voor deze prijslijst. Matches die je handmatig kiest in de review-queue verschijnen hier.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="thead-row">
                  <th className="text-left px-3 py-2 w-16">{magBeheren ? 'Aan' : 'Status'}</th>
                  <th className="text-left px-3 py-2">Tekst op fiche</th>
                  <th className="text-left px-3 py-2 min-w-[280px]">Werkpost</th>
                  <th className="text-right px-3 py-2">Gebruikt</th>
                  <th className="text-left px-3 py-2">Laatst</th>
                  {magBeheren && <th className="px-3 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {matches.map((m) => {
                  const aan = m.actief !== false;
                  const verplaatsen = verplaatsId === m.id;
                  return (
                    <Fragment key={m.id}>
                    <tr className={`${verplaatsen ? '' : 'border-b border-lightgrey last:border-b-0'} text-sm align-top ${aan ? '' : 'bg-lightgrey/60 text-secondary'}`}>
                      <td className="px-3 py-2">
                        {magBeheren ? (
                          <Schakelaar aan={aan} label={`Regel "${m.pdfNaamNormalized}" ${aan ? 'uitzetten' : 'aanzetten'}`}
                            disabled={updateMatch.isPending}
                            onChange={(actief) => updateMatch.mutate({ id: m.id, body: { actief } })} />
                        ) : (
                          <span className={aan ? 'chip-success' : 'chip-neutral'}>{aan ? 'aan' : 'uit'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{m.pdfNaamNormalized}</td>
                      <td className="px-3 py-2">
                        {m.plItemPdfNaam ? (
                          <>
                            <div>{m.plItemPdfNaam}</div>
                            <div className="text-xs text-secondary">{m.plItemNaamArbeid}</div>
                          </>
                        ) : (
                          <span className="chip-danger">item verwijderd</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{m.useCount}×</td>
                      <td className="px-3 py-2 text-secondary whitespace-nowrap">{fmtDate(m.lastUsedAt)}</td>
                      {magBeheren && (
                        <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                          {!verplaatsen && (
                            <button className="btn-ghost btn-sm" onClick={() => setVerplaatsId(m.id)} title="Deze tekst aan een andere post van deze prijslijst koppelen">
                              Verplaatsen
                            </button>
                          )}
                          <button className="btn-ghost btn-sm" onClick={() => forgetMatch.mutate(m.id)} disabled={forgetMatch.isPending}>Vergeten</button>
                        </td>
                      )}
                    </tr>
                    {verplaatsen && (
                      <tr className="border-b border-lightgrey last:border-b-0 bg-primary-50">
                        <td colSpan={6} className="px-3 pt-2 pb-3">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-sm font-semibold">Verplaatsen naar</span>
                            <div className="flex-1 min-w-[260px] max-w-2xl">
                              <PricelistItemPicker
                                items={posten}
                                value={m.plItemId}
                                size="sm"
                                placeholder={detailQ.isLoading ? 'Posten laden…' : 'Zoek de juiste post van deze prijslijst…'}
                                disabled={detailQ.isLoading || updateMatch.isPending}
                                onChange={(plItemId) => {
                                  if (plItemId && plItemId !== m.plItemId) updateMatch.mutate({ id: m.id, body: { plItemId } });
                                }}
                              />
                            </div>
                            <button className="btn-ghost btn-sm" onClick={() => setVerplaatsId(null)}>Annuleren</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="text-base font-semibold mb-1">Genegeerde regels</h2>
        <p className="text-xs text-secondary mb-3">
          Regels die een gebruiker met "Negeer" wegklikte. Worden bij een volgende fiche meteen overgeslagen in plaats van opnieuw te vragen. Uitgezet: de regel komt weer in de review-queue.
        </p>
        {learningQ.isLoading ? (
          <p className="text-sm text-secondary">Laden…</p>
        ) : skips.length === 0 ? (
          <p className="text-sm text-secondary">Nog geen genegeerde regels voor deze prijslijst.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="thead-row">
                  <th className="text-left px-3 py-2 w-16">{magBeheren ? 'Aan' : 'Status'}</th>
                  <th className="text-left px-3 py-2">Tekst op fiche</th>
                  <th className="text-right px-3 py-2">Gebruikt</th>
                  <th className="text-left px-3 py-2">Laatst</th>
                  {magBeheren && <th className="px-3 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {skips.map((s) => {
                  const aan = s.actief !== false;
                  return (
                    <tr key={s.id} className={`border-b border-lightgrey last:border-b-0 text-sm ${aan ? '' : 'bg-lightgrey/60 text-secondary'}`}>
                      <td className="px-3 py-2">
                        {magBeheren ? (
                          <Schakelaar aan={aan} label={`Genegeerde regel "${s.pdfNaamNormalized}" ${aan ? 'uitzetten' : 'aanzetten'}`}
                            disabled={updateSkip.isPending}
                            onChange={(actief) => updateSkip.mutate({ id: s.id, actief })} />
                        ) : (
                          <span className={aan ? 'chip-success' : 'chip-neutral'}>{aan ? 'aan' : 'uit'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{s.pdfNaamNormalized}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.useCount}×</td>
                      <td className="px-3 py-2 text-secondary whitespace-nowrap">{fmtDate(s.lastUsedAt)}</td>
                      {magBeheren && (
                        <td className="px-3 py-2 text-right">
                          <button className="btn-ghost btn-sm" onClick={() => forgetSkip.mutate(s.id)} disabled={forgetSkip.isPending}>Vergeten</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ExtraInfoRegelsKaart magBeheren={magBeheren} pricelists={pricelists} />
    </AppShell>
  );
}

/**
 * Geleerde regels voor het veld extra informatie en afwijkende eenheden (SPEC_calculatie 10): elke bevestiging
 * op een pop-up wordt een regel. Uitzetten, verwijderen en naar een andere post verplaatsen zonder code (10.2, 10.3).
 */
function ExtraInfoRegelsKaart({ magBeheren, pricelists }: { magBeheren: boolean; pricelists: Array<{ id: string; code?: string | null; name: string; versie?: number }> }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [verplaatsId, setVerplaatsId] = useState<string | null>(null);
  const regelsQ = useQuery({ queryKey: ['extra-info-regels'], queryFn: () => api.pricelists.extraInfoRegels() });
  const regels = regelsQ.data?.regels ?? [];
  const verplaatsRegel = regels.find((r) => r.id === verplaatsId) ?? null;
  // De posten van de familie: de gevellijst (codes) of de prijslijst met die code (ids).
  const doelLijst = verplaatsRegel
    ? [...pricelists].filter((pl) => (verplaatsRegel.familie === 'gevel' ? pl.code === 'gevel_A' : pl.code === verplaatsRegel.familie)).sort((a, b) => (b.versie ?? 0) - (a.versie ?? 0))[0]
    : undefined;
  const doelQ = useQuery({ queryKey: ['pricelist', doelLijst?.id], queryFn: () => api.pricelists.detail(doelLijst!.id), enabled: !!doelLijst });
  const posten = (doelQ.data?.pricelist.items ?? []).filter((it) => !it.isNvt && it.naamArbeid !== 'NVT');
  const invalidate = () => qc.invalidateQueries({ queryKey: ['extra-info-regels'] });
  const wijzig = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.pricelists.wijzigExtraInfoRegel>[1] }) => api.pricelists.wijzigExtraInfoRegel(id, body),
    onSuccess: (_r, { body }) => {
      toast.success(body.postCode !== undefined ? 'Verplaatst: de formulering wordt voortaan aan de nieuwe post gekoppeld' : body.actief ? 'Aangezet' : 'Uitgezet: de vraag komt weer');
      setVerplaatsId(null);
      invalidate();
    },
    onError: () => toast.error('Wijzigen mislukt'),
  });
  const verwijder = useMutation({
    mutationFn: (id: string) => api.pricelists.verwijderExtraInfoRegel(id),
    onSuccess: () => { toast.success('Verwijderd: de vraag komt bij de volgende fiche terug'); invalidate(); },
    onError: () => toast.error('Verwijderen mislukt'),
  });
  const familieNaam = (f: string) => (f === 'gevel' ? 'Gevellijst A/B' : pricelists.find((pl) => pl.code === f)?.name ?? f);
  const beslissing = (r: (typeof regels)[number]) =>
    r.beslissing === 'negeren' ? 'geen betaalbare post' : r.beslissing === 'zelfde_getal' ? `zelfde aantal in ${r.eenheid}` : r.postNaam || r.postCode || '—';

  return (
    <div className="card mt-5">
      <h2 className="text-base font-semibold mb-1">Extra informatie en eenheden</h2>
      <p className="text-xs text-secondary mb-3">
        Bevestigingen uit de pop-up bij het veld extra informatie en bij een andere eenheid dan de prijslijst. Dezelfde formulering levert geen vraag meer op;
        per calculatie staat in de log welke regel automatisch toegepast werd.
      </p>
      {regelsQ.isLoading ? <p className="text-sm text-secondary">Laden…</p> : regels.length === 0 ? (
        <p className="text-sm text-secondary">Nog geen regels. Ze ontstaan zodra iemand een herkende post bevestigt of negeert.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="thead-row">
                <th className="text-left px-3 py-2 w-16">{magBeheren ? 'Aan' : 'Status'}</th>
                <th className="text-left px-3 py-2">Op de fiche</th>
                <th className="text-left px-3 py-2 min-w-[220px]">Wordt</th>
                <th className="text-left px-3 py-2">Lijst</th>
                <th className="text-right px-3 py-2">Gebruikt</th>
                {magBeheren && <th className="px-3 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {regels.map((r) => (
                <Fragment key={r.id}>
                  <tr className={`border-b border-lightgrey last:border-b-0 text-sm align-top ${r.actief ? '' : 'bg-lightgrey/60 text-secondary'}`}>
                    <td className="px-3 py-2">
                      {magBeheren
                        ? <Schakelaar aan={r.actief} label={`Regel "${r.voorbeeld}" ${r.actief ? 'uitzetten' : 'aanzetten'}`} disabled={wijzig.isPending} onChange={(actief) => wijzig.mutate({ id: r.id, body: { actief } })} />
                        : <span className={r.actief ? 'chip-success' : 'chip-neutral'}>{r.actief ? 'aan' : 'uit'}</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="chip-neutral mr-2">{r.soort === 'tekst' ? 'extra informatie' : 'eenheid'}</span>
                      {r.voorbeeld}
                    </td>
                    <td className="px-3 py-2">{beslissing(r)}</td>
                    <td className="px-3 py-2 text-secondary whitespace-nowrap">{familieNaam(r.familie)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.useCount}×</td>
                    {magBeheren && (
                      <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                        {r.soort === 'tekst' && verplaatsId !== r.id && <button className="btn-ghost btn-sm" onClick={() => setVerplaatsId(r.id)}>Verplaatsen</button>}
                        <button className="btn-ghost btn-sm" onClick={() => verwijder.mutate(r.id)} disabled={verwijder.isPending}>Verwijderen</button>
                      </td>
                    )}
                  </tr>
                  {verplaatsId === r.id && (
                    <tr className="border-b border-lightgrey bg-primary-50">
                      <td colSpan={6} className="px-3 pt-2 pb-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-sm font-semibold">Koppelen aan</span>
                          <div className="flex-1 min-w-[260px] max-w-2xl">
                            <PricelistItemPicker
                              items={posten}
                              value={posten.find((it) => it.id === r.postCode || it.code === r.postCode)?.id ?? ''}
                              size="sm"
                              placeholder={doelQ.isLoading ? 'Posten laden…' : doelLijst ? 'Zoek de juiste post…' : 'Geen prijslijst gevonden voor deze regel'}
                              disabled={!doelLijst || doelQ.isLoading || wijzig.isPending}
                              onChange={(itemId) => {
                                const it = posten.find((x) => x.id === itemId);
                                if (!it) return;
                                wijzig.mutate({ id: r.id, body: { beslissing: 'post', postCode: r.familie === 'gevel' ? it.code ?? it.id : it.id, postNaam: it.naamArbeid || it.pdfNaam, eenheid: it.eenheid } });
                              }}
                            />
                          </div>
                          <button className="btn-ghost btn-sm" onClick={() => setVerplaatsId(null)}>Annuleren</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
